import React, { useEffect, useState, useCallback, useRef } from "react";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { Card, IconButton, Tooltip, Typography, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  getDsokMenus,
  addDsokMenu,
  deleteDsokMenu,
  getDsokMenu,
} from "app/services/api/menu";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import JumboSearch from "@jumbo/components/JumboSearch";
import AddBoxIcon from "@mui/icons-material/AddBox";

import { useNavigate } from "react-router-dom";
import MenuForm from "./MenuForm";
import DsokMenuItem from "./MenuItem";

const DsokMenu = () => {
  const { getAuthUser } = useJumboAuth();
  const { customerId } = getAuthUser();
  const { t } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const [searchName, setSearchName] = useState();
  const navigate = useNavigate();

  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useRef(true);

  const loadMenus = useCallback(async () => {
    try {
      const list = await getDsokMenus(customerId);
      const normalizedSearch = (searchName || "").toLowerCase();
      const filteredMenus = normalizedSearch
        ? (list || []).filter((menu) => {
            const rawTitle = menu?.title;
            const titleString =
              typeof rawTitle === "object" && rawTitle !== null
                ? String(Object.values(rawTitle)[0] || "")
                : String(rawTitle || "");
            return titleString.toLowerCase().includes(normalizedSearch);
          })
        : list;
      if (isMountedRef.current) {
        setMenus(filteredMenus || []);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [customerId, searchName]);

  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    loadMenus();
    return () => {
      isMountedRef.current = false;
    };
  }, [loadMenus]);

  const handleDelete = async (menu) => {
    hideDialog();
    setLoading(true);
    try {
      await deleteDsokMenu(menu.code);
      await loadMenus();
      const message = `${t("buttons.delete")} ${t(
        "notifications.wasSuccessful"
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("buttons.delete")} ${t("notifications.wasFailed")}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };

  const openModal = (type, menu) => {
    showDialog({
      variant: "default",
      title: t(`pages.menus.${type}`),
      sx: {
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
      },
      content: (
        <MenuForm
          menu={menu}
          onAddMenu={handleAddMenu}
          onEditMenu={handleEditMenu}
          onCancel={hideDialog}
        />
      ),
    });
  };

  const handleAddMenu = async (menu) => {
    setLoading(true);
    hideDialog();
    const object = {
      title: menu?.title,
      code: menu?.code,
    };

    try {
      const response = await addDsokMenu(object);
      if (response?.status === "Error") {
        const message = response?.message;
        enqueueSnackbar(message, {
          variant: "error",
        });
        setLoading(false);
        return;
      }
      const message = t("notifications.wasSuccessful");
      enqueueSnackbar(message, {
        variant: "success",
      });
      // Backend may not return created menu with _id; refetch list and find by code
      let newMenuId = response?._id ?? response?.id ?? response?.data?._id ?? response?.data?.id;
      if (!newMenuId && object?.code) {
        const list = await getDsokMenus();
        const created = Array.isArray(list)
          ? list.find((m) => (m?.code ?? m?.id) === object.code)
          : null;
        newMenuId = created?._id ?? created?.id;
      }
      if (newMenuId) {
        navigate(`/customer/dsok/menu/${newMenuId}`);
      } else {
        await loadMenus();
      }
    } catch (error) {
      const message = t("notifications.wasFailed");
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };
  const handleEditMenu = async (menu) => {
    setLoading(true);
    hideDialog();
    try {
      await getDsokMenu(menu?.code);
      await loadMenus();
      const message = t("notifications.wasSuccessful");
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = t("notifications.wasFailed");
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };

  const deleteConfirmation = (menu) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure"),
      onYes: () => handleDelete(menu),
      onNo: () => hideDialog(),
    });
  };

  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("sidebar.menuItem.dsokMenus")}
      </Typography>
      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        mb={2}
        spacing={1}
      >
        <Card sx={{ mb: 0, width: "50%" }}>
          <JumboSearch
            page="menus"
            onChange={setSearchName}
            sx={{
              width: "100%",
            }}
            placeholder={t("pages.presentation.searchPresentation")}
          />
        </Card>
        <Tooltip title={t("pages.menus.addMenu")}>
          <IconButton onClick={() => openModal("addMenu")}>
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {menus?.length ? (
        menus.map((menu, index) => {
          const menuId = menu?._id;
          return (
            <DsokMenuItem
              key={index}
              item={menu}
              disabled={loading}
              onDelete={deleteConfirmation}
              onEdit={() => {
                navigate(`/customer/dsok/menu/${menuId}`);
              }}
            />
          );
        })
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </React.Fragment>
  );
};

export default DsokMenu;
